import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "@/components/ui";
import { CircleAlert } from "lucide-react";
import { Blog } from "@/services/bedrock";
import { cn } from "@/lib/utils";

const PROFESSIONAL_MESSAGES = [
  "Warming up the typewriter...",
  "Reading the transcript...",
  "Finding the good parts...",
  "Gathering the key points...",
  "Outlining the article...",
  "Shaping the narrative...",
  "Writing the first draft...",
  "Drafting the introduction...",
  "Building out the sections...",
  "Smoothing out the sentences...",
  "Checking the flow...",
  "Tightening the copy...",
  "Adding a little sparkle...",
  "Preparing the preview...",
  "Almost ready to read...",
];

const FUN_MESSAGES = [
  "Reticulating splines...",
  "Herding the adjectives...",
  "Teaching the commas manners...",
  "Asking the muse for a second opinion...",
  "Polishing the punchlines...",
];

const MESSAGE_INTERVAL_MS = 3000;
const SLIDE_MS = 500;
const PROFESSIONAL_PER_CYCLE = 3;

type Rotation = {
  professional: string[];
  fun: string[];
  cycleIndex: number;
  last: string | null;
};

const shuffle = (messages: string[]): string[] => {
  const next = [...messages];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const refillDeck = (source: string[], avoid: string | null): string[] => {
  const deck = shuffle(source);
  if (avoid && deck.length > 1 && deck[0] === avoid) {
    const swapAt = deck.findIndex(message => message !== avoid);
    if (swapAt > 0) {
      [deck[0], deck[swapAt]] = [deck[swapAt], deck[0]];
    }
  }
  return deck;
};

const drawFrom = (deck: string[], source: string[], avoid: string | null): [string, string[]] => {
  const nextDeck = deck.length > 0 ? deck : refillDeck(source, avoid);
  return [nextDeck[0], nextDeck.slice(1)];
};

const takeNext = (rotation: Rotation): { message: string; rotation: Rotation } => {
  const useFun = rotation.cycleIndex >= PROFESSIONAL_PER_CYCLE;
  if (useFun) {
    const [message, fun] = drawFrom(rotation.fun, FUN_MESSAGES, rotation.last);
    return { message, rotation: { ...rotation, fun, cycleIndex: 0, last: message } };
  }

  const [message, professional] = drawFrom(rotation.professional, PROFESSIONAL_MESSAGES, rotation.last);
  return {
    message,
    rotation: { ...rotation, professional, cycleIndex: rotation.cycleIndex + 1, last: message },
  };
};

const createRotation = (): Rotation => ({
  professional: [],
  fun: [],
  cycleIndex: 0,
  last: null,
});

const labelClassName =
  "absolute inset-0 flex items-center justify-center text-lg font-semibold transition-[transform,opacity] duration-500 ease-in-out";

const WaitingLabels: React.FC = () => {
  const rotationRef = useRef<Rotation>(createRotation());
  const [current, setCurrent] = useState(() => {
    const drawn = takeNext(rotationRef.current);
    rotationRef.current = drawn.rotation;
    return drawn.message;
  });
  const [outgoing, setOutgoing] = useState<string | null>(null);
  const [shifted, setShifted] = useState(false);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const drawn = takeNext(rotationRef.current);
      rotationRef.current = drawn.rotation;
      setCurrent(from => {
        setOutgoing(from);
        setShifted(false);
        return drawn.message;
      });
    }, MESSAGE_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (outgoing == null) return;

    // Paint the incoming label below the fold first, then start the slide.
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => setShifted(true));
    });
    const timeoutId = window.setTimeout(() => {
      setOutgoing(null);
      setShifted(false);
    }, SLIDE_MS);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [outgoing]);

  return (
    <div className="relative h-10 w-full overflow-hidden">
      {outgoing != null && (
        <span className={cn(labelClassName, shifted ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100")}>
          {outgoing}
        </span>
      )}
      <span
        key={current}
        className={cn(
          labelClassName,
          outgoing == null || shifted ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        )}
      >
        {current}
      </span>
    </div>
  );
};

type Props = {
  blog: Blog | undefined;
  onRetry: () => void;
  retrying: boolean;
};

export const BlogProcessingOverlay: React.FC<Props> = ({ blog, onRetry, retrying }) => {
  const failedStep = useMemo(
    () => blog?.processingSteps?.find(step => step.status === "FAILED"),
    [blog?.processingSteps]
  );

  if (blog?.status === "FAILED" || failedStep) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 max-w-lg mx-auto text-center">
        <CircleAlert className="size-14 text-destructive shrink-0" />
        <div className="flex flex-col gap-2">
          <span className="text-lg font-semibold">{failedStep?.message || "We could not write this blog"}</span>
          <span className="text-sm text-muted-foreground">
            {failedStep?.details || blog?.generationError || "Something went wrong while writing."}
          </span>
        </div>
        {blog?.permittedEvents?.includes("regenerate") && (
          <Button onClick={onRetry} disabled={retrying}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 max-w-lg mx-auto text-center">
      <Spinner className="size-10 shrink-0 text-indigo-600" />
      <div className="flex w-full flex-col items-center gap-3">
        <WaitingLabels />
        <span className="text-sm text-muted-foreground">
          This usually takes under a minute. You can leave this page and come back whenever you are ready.
        </span>
      </div>
    </div>
  );
};
